import { useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, ExternalLink, Pencil, Plus, Trash2, Images } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { Button } from "@/components/ui/button";
import { Panel, EmptyState } from "@/components/core/AppLayout";
import { ConfirmDialog } from "@/components/core/ConfirmDialog";
import { CaseEditorDialog } from "./CaseEditorDialog";
import { PublishSwitch } from "./CaseFields";

const B = process.env.REACT_APP_BACKEND_URL;

const Row = ({ c, i, n, onMove, onEdit, onDelete, onToggle }) => {
  const pending = (c.kpi || []).filter((k) => !k.confermato).length;
  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-[#5FB3EA]/50 last:border-0" data-testid={`site-case-row-${c.slug}`}>
      <div className="flex flex-col">
        <Button variant="ghost" size="sm" disabled={i === 0} onClick={() => onMove(i, -1)} aria-label="Sposta su" data-testid={`site-case-up-${c.slug}`}><ArrowUp className="h-4 w-4" /></Button>
        <Button variant="ghost" size="sm" disabled={i === n - 1} onClick={() => onMove(i, 1)} aria-label="Sposta giù" data-testid={`site-case-down-${c.slug}`}><ArrowDown className="h-4 w-4" /></Button>
      </div>
      <div className="h-12 w-12 rounded-xl bg-[#0A1128] overflow-hidden flex items-center justify-center text-white text-xs font-black shrink-0">
        {c.logo_url || c.cliente_logo_url ? <img src={`${B}${c.logo_url || c.cliente_logo_url}`} alt="" className="h-full w-full object-contain bg-white" /> : c.nome.slice(0, 2).toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-bold text-[#0A1128] truncate">{c.nome}</div>
        <div className="text-xs text-slate-600">{c.settore?.it || "—"} · {(c.kpi || []).length} KPI
          {pending > 0 && <span className="ml-2 rounded-full bg-amber-300 px-2 py-0.5 text-[10px] font-bold text-[#0A1128]" data-testid={`site-case-pending-${c.slug}`}>{pending} da confermare</span>}
        </div>
      </div>
      <PublishSwitch checked={c.pubblicato} onChange={(v) => onToggle(c, v)} testid={`site-case-publish-${c.slug}`} />
      <div className="flex gap-1">
        {c.pubblicato && <a href={`/case-history/${c.slug}`} target="_blank" rel="noreferrer" className="h-9 w-9 rounded-full flex items-center justify-center hover:bg-[#8FD0FB]" aria-label="Apri sul sito" data-testid={`site-case-view-${c.slug}`}><ExternalLink className="h-4 w-4" /></a>}
        <Button variant="ghost" size="sm" onClick={() => onEdit(c)} aria-label="Modifica" data-testid={`site-case-edit-${c.slug}`}><Pencil className="h-4 w-4" /></Button>
        <Button variant="ghost" size="sm" onClick={() => onDelete(c)} aria-label="Elimina" data-testid={`site-case-delete-${c.slug}`}><Trash2 className="h-4 w-4 text-[#E8173A]" /></Button>
      </div>
    </div>
  );
};

export const CaseList = () => {
  const { data, setData, reload } = useFetch("/sito/case-history");
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const list = data || [];
  const move = async (i, d) => {
    const next = [...list];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    setData(next);
    try { await api.post("/sito/case-history/ordine", { ids: next.map((c) => c.id) }); } catch (e) { toast.error(errMsg(e)); reload(); }
  };
  const toggle = async (c, v) => {
    const body = { ...c, pubblicato: v };
    ["id", "logo_url", "cover_url", "gallery_urls", "ordine", "cliente_logo_url"].forEach((k) => delete body[k]);
    try { const r = await api.put(`/sito/case-history/${c.id}`, body); setData(list.map((x) => (x.id === c.id ? r.data : x))); toast.success(v ? "Pubblicata sul sito" : "Nascosta dal sito"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    try { await api.delete(`/sito/case-history/${del.id}`); setData(list.filter((x) => x.id !== del.id)); toast.success("Case history eliminata"); }
    catch (e) { toast.error(errMsg(e)); }
    setDel(null);
  };
  const saved = (c) => setData(list.some((x) => x.id === c.id) ? list.map((x) => (x.id === c.id ? c : x)) : [...list, c]);
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-sm text-slate-600">Ordina con le frecce: l'ordine è quello mostrato sul sito.</p>
        <Button onClick={() => setEdit({ item: null })} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] font-bold" data-testid="site-case-new"><Plus className="h-4 w-4 mr-1" />Nuova case history</Button>
      </div>
      <Panel className="overflow-hidden" data-testid="site-case-list">
        {data && !list.length ? <EmptyState icon={Images} title="Nessuna case history" text="Crea la prima case history da mostrare sul sito." testid="site-case-empty" />
          : list.map((c, i) => <Row key={c.id} c={c} i={i} n={list.length} onMove={move} onEdit={(x) => setEdit({ item: x })} onDelete={setDel} onToggle={toggle} />)}
      </Panel>
      <CaseEditorDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} item={edit?.item} onSaved={saved} />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Eliminare la case history?" description={`«${del?.nome}» verrà rimossa dal sito e dall'app. L'operazione non è reversibile.`} onConfirm={remove} />
    </>
  );
};
